import { CubePresenter, FlipIntent, Logger, Presenter } from 'src/3rds/cube'

import { Places } from 'src/Constants'
import { MainPresenter } from 'src/main/Main.presenter'
import { podeBloquearNumeros } from 'src/modules/bloqueios'
import { podeCadastrarEmLote } from 'src/modules/cadastro-lote/CadastroLote.presenter'
import { podeGerenciarCampanhas } from 'src/modules/campanha/Campanha.presenter'
import { podeVerDashboards } from 'src/modules/dashboards/Dashboards.perspectivas'
import { podeGerirGruposManuais } from 'src/modules/grupo-manual/GrupoManual.presenter'
import { podeMandarMensagemDireta } from 'src/modules/mensagem-direta'
import { podeGerirTemplates } from 'src/modules/templates'
import { podeUsarSimulador } from 'src/simulador'
import { AppStorage } from 'src/utils'

import OverviewKeys from './overview_keys'
import { atalhosPermitidos, dataPorExtenso, saudacao } from './overview.regras'
import { OverviewService } from './overview.service'
import { type Destino, HeaderScope, OverviewScope } from './overview_scopes'

const LOG = Logger.get('OverviewPresenter')
const service = OverviewService.singleton()
const appStorage = AppStorage.singleton()

/**
 * Painel: a tela que abre depois do login. Mostra o que pede ação agora, os números do dia e os atalhos para os
 * módulos que o papel pode abrir. É recarregado a cada vez que se volta a ele.
 */
export class OverviewPresenter extends CubePresenter<MainPresenter, OverviewScope> {
    private readonly __headerBar: Presenter<HeaderScope, OverviewPresenter>

    public constructor(app: MainPresenter) {
        super(app, new OverviewScope())
        this.__headerBar = new Presenter(this, this.scope.header)
    }

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new OverviewKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope
        keys.parentSlot(this.scope)

        if (initialization) {
            this.scope.onAtualizar = this.action(this.#carregar, this)
            this.scope.onAbrir = this.action(this.#abrir, this)
            this.scope.atalhos = atalhosPermitidos({
                dashboards: podeVerDashboards(),
                campanha: podeGerenciarCampanhas(),
                cadastroLote: podeCadastrarEmLote(),
                grupoManual: podeGerirGruposManuais(),
                bloqueios: podeBloquearNumeros(),
                templates: podeGerirTemplates(),
                mensagemDireta: podeMandarMensagemDireta(),
                simulador: podeUsarSimulador()
            })
        }
        // A cada volta ao painel, os números do momento.
        this.#carregar().catch((error) => LOG.error('Carregando o painel', error))
        return keys.allow
    }

    async #carregar() {
        const agora = new Date()
        this.scope.saudacao = saudacao(agora, appStorage.userName)
        this.scope.hoje = dataPorExtenso(agora)
        this.scope.carregando = true
        try {
            this.scope.painel = await service.painel()
            this.scope.erro = undefined
        } catch (error) {
            LOG.error('Carregando o painel', error)
            this.scope.erro = 'Não foi possível carregar o painel agora. Tente atualizar em instantes.'
        } finally {
            this.scope.carregando = false
        }
    }

    async #abrir(destino: Destino) {
        const place = Places[destino]
        if (place) await this.flip(place)
    }
}

export default OverviewPresenter
